import { cn } from '@heroui/react';
import type React from 'react';
import { Table, type TableSort } from './table.tsx';

type ResponsiveTableBreakpoint = 'md' | 'lg' | 'xl';

const tableVisibility: Record<ResponsiveTableBreakpoint, string> = {
  md: 'hidden md:block',
  lg: 'hidden lg:block',
  xl: 'hidden xl:block',
};

const tileVisibility: Record<ResponsiveTableBreakpoint, string> = {
  md: 'md:hidden',
  lg: 'lg:hidden',
  xl: 'xl:hidden',
};

export function ResponsiveTable({
  breakpoint = 'lg',
  tiles,
  children,
  ...tableProps
}: {
  breakpoint?: ResponsiveTableBreakpoint;
  tiles: React.ReactNode;
  bleed?: boolean;
  dense?: boolean;
  grid?: boolean;
  currentSort?: TableSort;
  setCurrentSort?: (tableSort: TableSort) => void;
} & React.ComponentPropsWithoutRef<'div'>) {
  return (
    <>
      <div className={tableVisibility[breakpoint]}>
        <Table {...tableProps}>{children}</Table>
      </div>
      <div className={tileVisibility[breakpoint]}>{tiles}</div>
    </>
  );
}

export function TableTileList({
  children,
  empty,
  emptyContent,
}: {
  children: React.ReactNode;
  empty?: boolean;
  emptyContent?: React.ReactNode;
}) {
  if (empty) {
    return emptyContent;
  }

  return <div className='grid gap-3'>{children}</div>;
}

export function TableTile({
  onClick,
  className,
  children,
}: {
  onClick?: () => void;
  className?: string;
  children: React.ReactNode;
}) {
  const isClickable = Boolean(onClick);

  return (
    <div
      role={isClickable ? 'button' : undefined}
      tabIndex={isClickable ? 0 : undefined}
      onClick={onClick}
      onKeyDown={event => {
        if (!isClickable) {
          return;
        }
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      className={cn(
        'w-full rounded-xl border border-zinc-200 bg-white p-4 text-left whitespace-normal',
        isClickable && 'cursor-pointer hover:bg-gray-50',
        className,
      )}
    >
      {children}
    </div>
  );
}

export function TableTileField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className='min-w-0'>
      <p className='mb-0.5 text-xs font-medium text-zinc-500'>{label}</p>
      <div className='text-sm text-zinc-700 break-words'>{children}</div>
    </div>
  );
}
